import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Input, Select, Modal, Typography, Space, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import "./RoiKpi.css";
import { kFormatter, getWindowLocationPath } from "../../../redux/constants";
import { useDispatch, useSelector } from "react-redux";
import RoiEffortsGrid from "./RoiEffortsGrid";
import {
  setRoiDetailsInBrandValue,
  setRoiDetailsInShareBrandsValue,
  setRoiKpiDataAPI,
} from "../../../redux/actions/ApiCall/apiCall";

const { Title, Text } = Typography;
const { Option } = Select;

// Currency options with better formatting
const currencyOptions = [
  { value: "USD", label: "USD - US Dollar", symbol: "$" },
  { value: "EUR", label: "EUR - Euro", symbol: "€" },
  { value: "GBP", label: "GBP - British Pound", symbol: "£" },
  { value: "JPY", label: "JPY - Japanese Yen", symbol: "¥" },

  // 🌍 Popular Global Currencies
  { value: "INR", label: "INR - Indian Rupee", symbol: "₹" },
  { value: "CAD", label: "CAD - Canadian Dollar", symbol: "$" },
  { value: "AUD", label: "AUD - Australian Dollar", symbol: "$" },
  { value: "CNY", label: "CNY - Chinese Yuan", symbol: "¥" },
  { value: "CHF", label: "CHF - Swiss Franc", symbol: "CHF" },

  // 🌍 Middle Eastern Currencies
  { value: "AED", label: "AED - UAE Dirham", symbol: "د.إ" },
  { value: "SAR", label: "SAR - Saudi Riyal", symbol: "ر.س" },
  { value: "QAR", label: "QAR - Qatari Riyal", symbol: "ر.ق" },
  { value: "KWD", label: "KWD - Kuwaiti Dinar", symbol: "د.ك" },
  { value: "OMR", label: "OMR - Omani Rial", symbol: "ر.ع" },
  { value: "BHD", label: "BHD - Bahraini Dinar", symbol: "د.ب" },
];

const RoiKpi = ({
  kpi_data,
  PdfDownloadStatus,
  ppt_generating_status,
  isPreviewState,
  widgetMetaData = null,
  section_index,
  widget_index,
  isDeepDiveDashboardView = false,
}) => {
  const dispatch = useDispatch();
  const authParams = useSelector((state) => state?.Authentication?.authParams);
  const isScheduleReportOpen = useSelector(
    (state) => state?.ApiCall?.is_schedule_report_open
  );
  const brandApiResult = useSelector(
    (state) => state?.ApiCall?.brands?.data?.result
  );
  const roiDetails = useSelector(
    (state) => state?.ApiCall?.brands?.data?.result?.roi_details
  );
  const shareBrands = useSelector((state) => state.ApiCall.shareBrands);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [currency, setCurrency] = useState(null);
  const [costPerHour, setCostPerHour] = useState(null);
  const [gridCalculatedValue, setGridCalculatedValue] = useState(0);
  const [totalTimeTaken, setTotalTimeTaken] = useState(0);
  const [currentGridData, setCurrentGridData] = useState([]);
  const [modalOpenCounter, setModalOpenCounter] = useState(0); // Add counter for modal resets

  // Temporary state for modal (only updates on Save)
  const [tempCurrency, setTempCurrency] = useState(null);
  const [tempCostPerHour, setTempCostPerHour] = useState(null);
  const [tempGridCalculatedValue, setTempGridCalculatedValue] = useState(0);
  const [tempCurrentGridData, setTempCurrentGridData] = useState([]);
  const [tempTotalTimeTaken, setTempTotalTimeTaken] = useState(0);

  const roiDetailsData = shareBrands ? shareBrands?.roi_details : roiDetails;
  // console.log("roiDetailsData", roiDetailsData);

  // Suppress ResizeObserver errors that don't affect functionality
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    };
    window.addEventListener("error", handleResizeObserverError);
    return () => window.removeEventListener("error", handleResizeObserverError);
  }, []);

  // Function to calculate initial grid value from dynamic data or stored efforts
  const calculateInitialGridValue = (gridData, efforts, costPerHourValue) => {
    if (
      !costPerHourValue ||
      !gridData ||
      !gridData.dataSource ||
      !Array.isArray(gridData.dataSource)
    ) {
      return { costSaving: 0, totalTime: 0 };
    }

    // Use dynamic grid data
    let dataToProcess = gridData.dataSource.map((item, index) => ({
      action_type: item["Action Type"] || "Internal Actions",
      times_taken: parseInt(item["Number of Actions"]) || 0,
      efforts_required: 1, // Default value
    }));

    // Apply stored efforts if available
    if (efforts && typeof efforts === "object" && !Array.isArray(efforts)) {
      dataToProcess = dataToProcess.map((item) => {
        const effortValue = efforts[item.action_type];
        return effortValue !== undefined
          ? { ...item, efforts_required: effortValue }
          : item;
      });
    }

    // Calculate total time taken
    const calculatedTotalTime = dataToProcess.reduce((sum, item) => {
      return sum + item.times_taken * item.efforts_required;
    }, 0);

    // Calculate cost saving
    const costSaving = (
      (calculatedTotalTime / 60) *
      parseFloat(costPerHourValue)
    ).toFixed(2);
    return {
      costSaving: parseFloat(costSaving),
      totalTime: calculatedTotalTime,
    };
  };

  useEffect(() => {
    const storedCurrency =
      roiDetailsData?.currency || localStorage.getItem("currencyEmployees");
    const storedCostPerHour =
      roiDetailsData?.cost_per_hour || localStorage.getItem("costToEmployees");
    if (!storedCurrency) {
      setCurrency("USD");
      setCostPerHour("5.00");
    } else {
      if (storedCurrency) {
        setCurrency(storedCurrency);
      }
      if (storedCostPerHour) {
        setCostPerHour(storedCostPerHour);
      }
    }
  }, [isSettingsOpen]);

  const roiGridFromMetaData = useMemo(() => {
    if (!widgetMetaData) return null;
    let pathName = getWindowLocationPath()?.toLowerCase();
    let graphData =
      pathName === "edit-dashboard" || pathName === "create-dashboard"
        ? widgetMetaData?.data
        : widgetMetaData?.graphData || widgetMetaData?.persist_graphdata;
    let gridData =
      typeof graphData === "string"
        ? graphData
        : Array.isArray(graphData?.data["roi_grid"])
        ? graphData?.data["roi_grid"]?.[0]
        : graphData?.data["roi_grid"];
    return gridData ? gridData : null;
  }, [widgetMetaData]);
  // console.log("roiGridFromMetaData : ", roiGridFromMetaData);

  // Calculate initial grid value when component mounts or data changes
  useEffect(() => {
    if (currency && costPerHour) {
      const initialCalculation = calculateInitialGridValue(
        roiGridFromMetaData,
        roiDetailsData?.efforts,
        costPerHour
      );
      setGridCalculatedValue(initialCalculation.costSaving);
      setTotalTimeTaken(initialCalculation.totalTime);
    }
  }, [currency, costPerHour, roiGridFromMetaData, roiDetailsData?.efforts]);

  const handleSettingsClick = () => {
    // Initialize temporary values with current saved values
    setTempCurrency(currency);
    setTempCostPerHour(costPerHour);
    setTempGridCalculatedValue(gridCalculatedValue);
    setTempCurrentGridData([...currentGridData]);
    setTempTotalTimeTaken(totalTimeTaken);
    setModalOpenCounter((prev) => prev + 1); // Increment counter to force reset
    setIsSettingsOpen(true);
  };

  const handleCancel = () => {
    // Reset temporary values - no changes to main state
    setTempCurrency(currency);
    setTempCostPerHour(costPerHour);
    setTempGridCalculatedValue(gridCalculatedValue);
    setTempCurrentGridData([...currentGridData]);
    setTempTotalTimeTaken(totalTimeTaken);
    setIsSettingsOpen(false);
  };

  const handleDone = () => {
    // Update main state with temporary values
    setCurrency(tempCurrency);
    setCostPerHour(tempCostPerHour);
    setGridCalculatedValue(tempGridCalculatedValue);
    setCurrentGridData([...tempCurrentGridData]);
    setTotalTimeTaken(tempTotalTimeTaken);

    setIsSettingsOpen(false);
    // Prepare efforts data in the required format
    const effortsData = {};
    if (tempCurrentGridData && Array.isArray(tempCurrentGridData)) {
      tempCurrentGridData.forEach((item) => {
        effortsData[item.action_type] = item.efforts_required;
      });
    }
    let updatedRoiDetails = {
      currency: tempCurrency,
      cost_per_hour: tempCostPerHour,
      efforts: effortsData,
    };
    localStorage.setItem("currencyEmployees", tempCurrency);
    localStorage.setItem("costToEmployees", tempCostPerHour);
    // Also need to updated in brand overall state
    if (shareBrands) {
      dispatch(setRoiDetailsInShareBrandsValue(updatedRoiDetails));
    } else if (brandApiResult) {
      dispatch(setRoiDetailsInBrandValue(updatedRoiDetails));
    }

    // API Call to save the data
    let obj = {
      authParams: authParams,
      currency: tempCurrency,
      cost_per_hour: tempCostPerHour,
      efforts: effortsData,
    };
    dispatch(setRoiKpiDataAPI(obj));
  };

  // Fixed Cost Saving calculation logic
  const baseValue = kpi_data?.value === 0 ? 0 : kpi_data?.value; // Default value if kpi_data is not provide
  // Always show grid calculation if available, otherwise fall back to base calculation
  const calculatedValue =
    gridCalculatedValue > 0
      ? gridCalculatedValue
      : baseValue > 0
      ? (baseValue * parseFloat(costPerHour || "5")).toFixed(2)
      : "0.00";
  const currencySymbol =
    currencyOptions.find((option) => option.value === currency)?.symbol || "";

  const handleCurrencyChange = useCallback((value) => {
    try {
      setTempCurrency(value);
    } catch (error) {
      console.warn("Currency change error:", error);
    }
  }, []);

  const handleCostToEmployeesChange = useCallback((value) => {
    try {
      // Validate input to prevent invalid values
      if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
        setTempCostPerHour(value);
      }
    } catch (error) {
      console.warn("Cost per hour change error:", error);
    }
  }, []);

  const handleGridCalculationChange = useCallback(
    (costSaving, totalTime, gridData) => {
      try {
        setTempGridCalculatedValue(costSaving);
        setTempTotalTimeTaken(totalTime);
        setTempCurrentGridData(gridData || []);
      } catch (error) {
        console.warn("Grid calculation change error:", error);
      }
    },
    []
  );

  //   console.log("kpi_data", kpi_data);

  // Temporary calculated value for modal preview (uses temp values)
  const tempCalculatedValue = useMemo(() => {
    try {
      if (!tempCurrency || !tempCostPerHour) return "0.00";

      const baseValue = kpi_data?.value === 0 ? 0 : kpi_data?.value;
      const result =
        tempGridCalculatedValue > 0
          ? tempGridCalculatedValue
          : baseValue > 0
          ? (baseValue * parseFloat(tempCostPerHour || "5")).toFixed(2)
          : "0.00";
      return result;
    } catch (error) {
      console.warn("Temp calculated value error:", error);
      return "0.00";
    }
  }, [tempCurrency, tempCostPerHour, tempGridCalculatedValue, kpi_data?.value]);

  const tempCurrencySymbol = useMemo(() => {
    try {
      return (
        currencyOptions.find((option) => option.value === tempCurrency)
          ?.symbol || ""
      );
    } catch (error) {
      console.warn("Temp currency symbol error:", error);
      return "";
    }
  }, [tempCurrency]);

  return (
    <>
      <div className="roi-kpi-card">
        <div className="roi-kpi-header">
          <div className="roi-kpi-icon">
            <div className="roi-icon-square"></div>
          </div>
          <h3 className="roi-kpi-title">
            {kpi_data?.text}
            {kpi_data?.info_text ? (
              <Tooltip
                title={kpi_data?.info_text}
                placement="top"
                className="text-ellipsis d-inline-block wm-chart-card-title"
              >
                <i className="fa fa-info-circle cursor-pointer ml-2" />
              </Tooltip>
            ) : (
              ""
            )}
          </h3>

          <Tooltip
            title={
              isPreviewState ||
              ppt_generating_status ||
              PdfDownloadStatus ||
              isScheduleReportOpen ||
              window.location.pathname === "/share"
                ? "ROI settings allowed only in view dashboard"
                : ""
            }
          >
            <Button
              disabled={
                isPreviewState
                  ? true
                  : ppt_generating_status
                  ? true
                  : PdfDownloadStatus
                  ? true
                  : isScheduleReportOpen
                  ? true
                  : window.location.pathname === "/share"
                  ? true
                  : false
              }
              type="text"
              icon={<SettingOutlined />}
              className="roi-settings-btn"
              onClick={handleSettingsClick}
            />
          </Tooltip>
        </div>

        <div className="roi-kpi-content">
          <p className="roi-kpi-subtitle">Cost Saving</p>
          <div className="roi-kpi-metrics">
            <Tooltip title={calculatedValue}>
              <span className="roi-amount">
                {currencySymbol}
                {kFormatter(calculatedValue)}
              </span>
            </Tooltip>
            {kpi_data?.percent_change ? (
              <span className="roi-percentage">
                {kpi_data?.percent_change}%
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>

      <Modal
        title={
          <div className="roi-modal-header">
            <Title level={4} style={{ margin: 0, color: "#374151" }}>
              Calculate ROI
            </Title>
            <Text type="secondary">
              Configure your preferred currency and cost per hour
            </Text>
          </div>
        }
        open={isSettingsOpen}
        onCancel={handleCancel}
        maskClosable={false}
        className="roi-settings-dialog"
        width={920}
        height="85vh"
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
            className="roi-cancel-btn"
            size="large"
          >
            Cancel
          </Button>,
          <Button
            disabled={!tempCurrency || !tempCostPerHour}
            key="done"
            type="primary"
            onClick={handleDone}
            className="roi-done-btn"
            size="large"
          >
            Save
          </Button>,
        ]}
      >
        <div className="roi-settings-content">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {/* Single row with Currency, Cost per Hour, Total time Saved, and Cost Saving */}
            <div className="roi-form-row">
              <div className="roi-form-group roi-form-fourth">
                <Text className="roi-label">Currency</Text>
                <Select
                  showSearch
                  value={tempCurrency}
                  onChange={handleCurrencyChange}
                  className="roi-select-enhanced"
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="Select currency"
                  optionLabelProp="label"
                  dropdownClassName="roi-currency-dropdown"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {currencyOptions.map((option) => (
                    <Option
                      key={option.value}
                      value={option.value}
                      label={`${option.symbol} ${option.value}`}
                    >
                      <div className="roi-currency-option">
                        <span className="roi-currency-full">
                          {option.label}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="roi-form-group roi-form-fourth">
                <Text className="roi-label">Cost per Hour</Text>
                <Input
                  type="number"
                  value={tempCostPerHour}
                  onChange={(e) => handleCostToEmployeesChange(e.target.value)}
                  className="roi-input-enhanced"
                  size="large"
                  placeholder="5.00"
                  prefix={tempCurrencySymbol}
                  min="0"
                  step="0.01"
                  onKeyDown={(e) => {
                    // Prevent ResizeObserver issues with certain key combinations
                    if (e.key === "Tab") {
                      try {
                        e.preventDefault();
                        setTimeout(() => {
                          const nextInput = e.target
                            .closest(".roi-form-row")
                            .querySelector(".roi-select-enhanced");
                          if (nextInput) nextInput.focus();
                        }, 0);
                      } catch (error) {
                        console.warn("Key down error:", error);
                      }
                    }
                  }}
                />
                {/* <Text type="secondary" className="roi-helper-text">
                  How many {currency} equals 5 USD
                </Text> */}
              </div>

              <div className="roi-form-group roi-form-fourth">
                <Text className="roi-label">Total Time Saved</Text>
                <div className="roi-preview-card-inline">
                  <Tooltip title={`${tempTotalTimeTaken} minutes`}>
                    <Text strong className="roi-preview-value-inline">
                      {(tempTotalTimeTaken / 60).toFixed(2)} hrs
                    </Text>
                  </Tooltip>
                </div>
              </div>

              <div className="roi-form-group roi-form-fourth">
                <Text className="roi-label">Cost Saving</Text>
                <div className="roi-preview-card-inline">
                  <Tooltip title={tempCalculatedValue}>
                    <Text strong className="roi-preview-value-inline">
                      {tempCurrencySymbol}
                      {kFormatter(tempCalculatedValue)}
                    </Text>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* ROI Efforts Grid Component */}
            <RoiEffortsGrid
              key={`roi-grid-${modalOpenCounter}`}
              currency={tempCurrency}
              costPerHour={tempCostPerHour}
              onCalculationChange={handleGridCalculationChange}
              defaultEfforts={roiDetailsData?.efforts}
              dynamicGridData={roiGridFromMetaData}
            />

            {/* <div className="roi-preview-section">
              <div className="roi-preview-card">
                <Text type="secondary">Cost Saving</Text>
                <Tooltip title={calculatedValue}>
                  <Text strong className="roi-preview-value">
                    {currencySymbol}
                    {kFormatter(calculatedValue)}
                  </Text>
                </Tooltip>
              </div>
            </div> */}
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default RoiKpi;
